import { Badge, QualityBar } from '@/components/Badge';
import { Card, CardBody, CardHeader, CardTitle, KeyValue, Stat } from '@/components/Card';
import type { Assessment } from '@/domain/engine';
import {
  formatCompact,
  formatCurrency,
  formatMass,
  formatMinutes,
  formatNumber,
  formatPercent,
  formatRatio,
} from '@/lib/format';
import { MarginList } from './MarginList';
import { MetricCard } from './MetricCard';
import { MissingHints } from './MissingHints';
import { VerdictBanner } from './VerdictBanner';

const SOURCE_LABEL: Record<'input' | 'estimated' | 'mixed' | 'none', { text: string; tone: 'ok' | 'caution' | 'neutral' }> = {
  input: { text: '实测值', tone: 'ok' },
  estimated: { text: '经验估算', tone: 'caution' },
  mixed: { text: '实测 + 估算', tone: 'caution' },
  none: { text: '无数据', tone: 'neutral' },
};

/** 评估结果面板：结论 → 指标 → 裕度 → 缺失参数 */
export function ResultPanel({ assessment }: { assessment: Assessment }) {
  const { mass, thrust, twr, hover, power, endurance, cost, reservePct } = assessment;

  const massTotal = mass.value ? formatMass(mass.value.totalG) : null;
  const thrustTotal = thrust.value ? formatMass(thrust.value.totalG) : null;
  const thrustSource = thrust.value ? SOURCE_LABEL[thrust.value.source] : null;
  const perMotor = thrust.value?.perMotorG ?? null;

  return (
    <div className="flex flex-col gap-3">
      <VerdictBanner assessment={assessment} />

      <MetricCard
        title="预估推力"
        subtitle="四轴按全部电机最大推力汇总"
        value={thrustTotal ? thrustTotal.value : '—'}
        unit={thrustTotal?.unit ?? 'g'}
        badge={thrustSource ? <Badge tone={thrustSource.tone}>{thrustSource.text}</Badge> : undefined}
        missing={thrust.missing}
        notes={thrust.notes}
      >
        <KeyValue
          columns={2}
          items={[
            { label: '单电机推力', value: perMotor !== null ? `${formatCompact(perMotor)} g` : '—' },
            { label: '电机数量', value: thrust.value ? `${formatNumber(thrust.value.motorCount, 0)} 个` : '—' },
            {
              label: '预留后可用推力',
              value: twr.value ? `${formatCompact(twr.value.usableThrustG)} g` : '—',
            },
            { label: '安全系数预留', value: formatPercent(reservePct, 0) },
          ]}
        />
      </MetricCard>

      <MetricCard
        title="预估起飞质量"
        subtitle="全部部件重量 × 数量之和"
        value={massTotal ? massTotal.value : '—'}
        unit={massTotal?.unit ?? 'g'}
        missing={mass.missing}
        notes={mass.notes}
      >
        {mass.value ? (
          <>
            <KeyValue
              columns={3}
              items={[
                { label: '空载质量', value: `${formatMass(mass.value.emptyG).value} ${formatMass(mass.value.emptyG).unit}` },
                { label: '载荷质量', value: `${formatMass(mass.value.payloadG).value} ${formatMass(mass.value.payloadG).unit}` },
                { label: '载荷占比', value: formatPercent(mass.value.totalG > 0 ? (mass.value.payloadG / mass.value.totalG) * 100 : 0, 0) },
              ]}
            />
            <div className="flex flex-col gap-1.5">
              {mass.value.byGroup.map((entry) => (
                <div key={entry.group} className="flex items-center gap-2">
                  <span className="w-14 shrink-0 text-[11px] text-fg-muted">{entry.label}</span>
                  <QualityBar
                    value={entry.grams}
                    max={mass.value?.totalG ?? 1}
                    tone="info"
                    ariaLabel={`${entry.label}重量占比`}
                  />
                  <span className="tabular w-20 shrink-0 text-right text-[11px] text-fg-muted">
                    {formatCompact(entry.grams)} g
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </MetricCard>

      <MetricCard
        title="推重比"
        subtitle="总推力 ÷ 起飞质量"
        value={formatRatio(twr.value?.effective ?? null)}
        tone={
          twr.value === null
            ? 'neutral'
            : twr.value.effective < 1
              ? 'critical'
              : twr.value.effective < 1.2
                ? 'warning'
                : twr.value.effective < 1.5
                  ? 'caution'
                  : 'ok'
        }
        missing={twr.missing}
        notes={twr.notes}
      >
        <KeyValue
          columns={2}
          items={[
            { label: '裸推重比', value: formatRatio(twr.value?.bare ?? null) },
            { label: '有效推重比', value: formatRatio(twr.value?.effective ?? null) },
            { label: '有效推力', value: twr.value ? `${formatCompact(twr.value.usableThrustG)} g` : '—' },
            { label: '安全系数预留', value: formatPercent(reservePct, 0) },
          ]}
        />
      </MetricCard>

      <MetricCard
        title="悬停油门"
        subtitle="推力与油门平方成正比，悬停油门 = √(1 ÷ 推重比)"
        value={hover.value ? formatPercent(hover.value.throttlePct, 0) : '—'}
        tone={hover.value ? (hover.value.feasible ? 'ok' : 'critical') : 'neutral'}
        badge={
          hover.value ? (
            <Badge tone={hover.value.feasible ? 'ok' : 'critical'} solid={!hover.value.feasible}>
              {hover.value.feasible ? '余量充足' : '余量不足'}
            </Badge>
          ) : undefined
        }
        missing={hover.missing}
        notes={hover.notes}
      >
        {hover.value ? (
          <div className="flex flex-col gap-2">
            <QualityBar
              value={Math.min(hover.value.throttlePct, 100)}
              tone={hover.value.feasible ? 'ok' : 'critical'}
              label={`悬停 ${formatPercent(hover.value.throttlePct, 0)}`}
              ariaLabel="悬停油门"
            />
            <KeyValue
              columns={2}
              items={[
                { label: '预留上限', value: formatPercent(hover.value.reserveCapPct, 0) },
                { label: '剩余余量', value: formatPercent(hover.value.headroomPct, 0) },
              ]}
            />
          </div>
        ) : null}
      </MetricCard>

      <MetricCard
        title="功率与电流"
        subtitle="用于估算续航与校验供电能力"
        value={formatNumber(power.value?.hoverPowerW ?? null, 0)}
        unit="W（悬停）"
        missing={power.missing}
        notes={power.notes}
      >
        <KeyValue
          columns={2}
          items={[
            { label: '标称电压', value: `${formatNumber(power.value?.voltageV ?? null, 1)} V` },
            { label: '满油门总电流', value: `${formatNumber(power.value?.maxCurrentA ?? null, 1)} A` },
            { label: '悬停电流', value: `${formatNumber(power.value?.hoverCurrentA ?? null, 1)} A` },
            {
              label: '电机效率',
              value:
                power.value?.efficiencyGPerW === null || power.value?.efficiencyGPerW === undefined
                  ? '—'
                  : `${formatNumber(power.value.efficiencyGPerW, 1)} g/W`,
            },
          ]}
        />
      </MetricCard>

      <MetricCard
        title="预估续航"
        subtitle="按悬停电流与电池可用容量估算"
        value={formatMinutes(endurance.value?.minutes ?? null)}
        missing={endurance.missing}
        notes={endurance.notes}
      >
        <KeyValue
          columns={2}
          items={[
            {
              label: '电池容量',
              value: endurance.value ? `${formatNumber(endurance.value.capacityAh, 2)} Ah` : '—',
            },
            {
              label: '可用放电比例',
              value: endurance.value ? formatPercent(endurance.value.usableRatio * 100, 0) : '—',
            },
          ]}
        />
      </MetricCard>

      <MetricCard
        title="预估金额"
        subtitle="全部部件价格 × 数量之和"
        value={formatCurrency(cost.value?.total ?? null)}
        missing={cost.missing}
        notes={cost.notes}
      >
        {cost.value ? (
          <>
            <KeyValue
              columns={2}
              items={[
                { label: '每 100 g 起飞质量', value: formatCurrency(cost.value.per100g) },
                { label: '部件条目', value: `${formatNumber(cost.value.lines.length, 0)} 项` },
              ]}
            />
            <div className="flex flex-col gap-1.5">
              {cost.value.byGroup.map((entry) => (
                <div key={entry.group} className="flex items-center gap-2">
                  <span className="w-14 shrink-0 text-[11px] text-fg-muted">{entry.label}</span>
                  <QualityBar
                    value={entry.amount}
                    max={cost.value?.total ?? 1}
                    tone="accent"
                    ariaLabel={`${entry.label}成本占比`}
                  />
                  <span className="tabular w-20 shrink-0 text-right text-[11px] text-fg-muted">
                    {formatCurrency(entry.amount)}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-1 border-t border-line pt-2">
              {cost.value.lines.map((line) => (
                <div key={`${line.label}-${line.amount}`} className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-[11px] text-fg-muted">
                    {line.label}
                    <span className="tabular ml-1 text-fg-subtle">×{line.qty}</span>
                  </span>
                  <span className="tabular shrink-0 text-[11px] font-medium text-fg">
                    {formatCurrency(line.amount)}
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </MetricCard>

      <MarginList margins={assessment.margins} />

      {assessment.missingAll.length > 0 ? (
        <Card className="print-keep">
          <CardHeader
            actions={<Badge tone="caution">{assessment.missingAll.length} 项</Badge>}
          >
            <CardTitle subtitle="这些参数没有填，相关指标未把它们计入">缺失参数汇总</CardTitle>
          </CardHeader>
          <CardBody className="flex flex-col gap-2">
            <MissingHints missing={assessment.missingAll} />
            <p className="text-[11px] leading-relaxed text-fg-subtle">
              参数留空不会影响其它指标的计算，随时补齐即可看到结果更新。
            </p>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardBody className="flex flex-col gap-1.5">
          <Stat label="数据完整度" value={formatPercent(assessment.completeness * 100, 0)} size="sm" />
          <p className="text-[11px] leading-relaxed text-fg-subtle">
            结果为搭建前的快速可行性参考：经验估算误差约 ±20%，实际推力还受桨叶形变、进气环境、电池电压跌落与海拔影响，请以实测为准。
          </p>
        </CardBody>
      </Card>
    </div>
  );
}
